<template>
  <div class="p-4">

    <div v-if="!loaded" class="text-center py-4 text-muted">
      <i class="ri-loader-4-line"></i> Cargando...
    </div>

    <div v-else>

      <!-- Selector mes/año -->
      <div class="d-flex align-items-center mb-4 flex-wrap">
        <div class="mr-3 mb-2">
          <label class="label-form d-block">Mes</label>
          <select class="form-control form-control-sm" v-model="month" @change="loadData">
            <option v-for="(m, idx) in monthNames" :key="idx" :value="idx + 1">{{ m }}</option>
          </select>
        </div>
        <div class="mr-3 mb-2">
          <label class="label-form d-block">Año</label>
          <select class="form-control form-control-sm" v-model="year" @change="loadData">
            <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
          </select>
        </div>
      </div>

      <!-- Resumen -->
      <div class="row mb-4">
        <div class="col-6 col-md-3 mb-2">
          <div class="stat-card p-3 border rounded text-center">
            <div class="text-muted small">Días trabajados</div>
            <div class="font-weight-bold h4 mb-0 text-primary">{{ summary.daysWorked }}</div>
          </div>
        </div>
        <div class="col-6 col-md-3 mb-2">
          <div class="stat-card p-3 border rounded text-center">
            <div class="text-muted small">Faltas</div>
            <div class="font-weight-bold h4 mb-0 text-danger">{{ summary.totalAbsences }}</div>
          </div>
        </div>
        <div class="col-6 col-md-3 mb-2">
          <div class="stat-card p-3 border rounded text-center">
            <div class="text-muted small">Min. tardanza</div>
            <div class="font-weight-bold h4 mb-0 text-warning">{{ summary.totalTardinessMinutes }}</div>
          </div>
        </div>
        <div class="col-6 col-md-3 mb-2">
          <div class="stat-card p-3 border rounded text-center">
            <div class="text-muted small">Horas extra</div>
            <div class="font-weight-bold h4 mb-0 text-success">{{ summary.totalOvertimeHours }}</div>
          </div>
        </div>
      </div>

      <!-- Tabla de registros -->
      <h5 class="mb-3">Registros del mes</h5>
      <div class="table-responsive">
        <table class="custom-table">
          <thead>
            <tr>
              <th>FECHA</th>
              <th>ESTADO</th>
              <th>ENTRADA</th>
              <th>SALIDA</th>
              <th class="text-center">TARDANZA (min)</th>
              <th class="text-center">HRS EXTRA</th>
              <th class="text-center">HRS TRAB.</th>
              <th>OBSERVACIONES</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="records.length === 0">
              <td colspan="8" class="text-center text-muted py-3">No hay registros para este mes</td>
            </tr>
            <tr v-for="rec in records" :key="rec.id || rec.date">
              <td>{{ rec.recordDate || rec.date }}</td>
              <td>
                <span
                  class="att-badge"
                  :style="{ backgroundColor: statusColor(rec.attendanceStatus), color: '#fff' }"
                >{{ statusLabel(rec.attendanceStatus) }}</span>
              </td>
              <td>{{ rec.entryTime || '—' }}</td>
              <td>{{ rec.exitTime  || '—' }}</td>
              <td class="text-center">{{ rec.tardinessMinutes || 0 }}</td>
              <td class="text-center">{{ rec.overtimeHours   || 0 }}</td>
              <td class="text-center">{{ rec.hoursWorked     || 0 }}</td>
              <td>{{ rec.observations || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>
  </div>
</template>

<script>
import { Alert } from 'wize-admin'
import { AttendanceService } from '@/core/services'
import { Constants } from '@/core/utils'

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

export default {
  name: 'AttendanceTab',

  props: {
    employeeId: { type: Number, default: null },
  },

  data() {
    const now = new Date()
    return {
      loaded:     false,
      year:       now.getFullYear(),
      month:      now.getMonth() + 1,
      records:    [],
      monthNames: MONTH_NAMES,
      summary: {
        daysWorked:            0,
        totalAbsences:         0,
        totalTardinessMinutes: 0,
        totalOvertimeHours:    0,
      },
    }
  },

  computed: {
    years() {
      const current = new Date().getFullYear()
      return Array.from({ length: 4 }, (_, i) => current - 1 + i)
    },
  },

  methods: {
    statusColor(key) {
      const found = Object.values(Constants.AttendanceStatus).find(s => s.key === key)
      return found ? found.color : '#adb5bd'
    },
    statusLabel(key) {
      const found = Object.values(Constants.AttendanceStatus).find(s => s.key === key)
      return found ? found.letter : key
    },

    async loadData() {
      if (!this.employeeId) { this.loaded = true; return }
      try {
        this.loaded = false
        const resp = await AttendanceService.getMonthRecords(this.employeeId, this.year, this.month)
        this.records = resp.payload || []
        this.computeSummary()
      } catch (e) {
        Alert.error('Error al cargar registros de asistencia')
      } finally {
        this.loaded = true
      }
    },

    computeSummary() {
      let daysWorked = 0, absences = 0, tardiness = 0, overtime = 0
      this.records.forEach(r => {
        if (r.attendanceStatus === 'ASISTIO')           daysWorked++
        if (r.attendanceStatus === 'FALTA_JUSTIFICADA' ||
            r.attendanceStatus === 'FALTA_INJUSTIFICADA') absences++
        tardiness += r.tardinessMinutes || 0
        overtime  += parseFloat(r.overtimeHours || 0)
      })
      this.summary = {
        daysWorked,
        totalAbsences:         absences,
        totalTardinessMinutes: tardiness,
        totalOvertimeHours:    overtime.toFixed(2),
      }
    },
  },

  mounted() {
    this.loadData()
  },
}
</script>

<style scoped lang="stylus">
.att-badge
  display inline-block
  padding 2px 8px
  border-radius 12px
  font-size 11px
  font-weight 700
</style>
